import { SERVICES, HOW_WE_RUN_IT, WHY_US } from "@/content/thoratech";
import { Check, ArrowUpRight } from "lucide-react";

export function Difference() {
  return (
    <section
      id="difference"
      data-testid="difference-section"
      className="relative py-24 md:py-32 bg-white border-t border-[#01080E]/10"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16">
          <div className="md:col-span-4">
            <div className="inline-flex items-center gap-3 mb-5">
              <span className="block h-px w-8 bg-[#2AC0A5]" />
              <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[#01080E]">
                The Thoratech difference
              </span>
            </div>
            <h2 className="font-display text-3xl md:text-5xl font-bold leading-[1.05] text-[#01080E]">
              Most outsourcers give you seats.
              <br />
              <span className="text-[#2AC0A5]">We give you governance.</span>
            </h2>
          </div>
          <div className="md:col-span-7 md:col-start-6">
            <p className="text-xl leading-relaxed text-[#01080E]/80">
              Every process we run is measured, reviewed and accountable to a service-level agreement,
              so you see outcomes, not just activity. We sit on your side of the table: one
              engagement lead owns the number, a dedicated team runs the work and dashboards show
              you exactly where things stand.
            </p>
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-px bg-[#01080E]/10 border border-[#01080E]/10">
              {[
                { k: "Owned", v: "Outcomes" },
                { k: "Backed by", v: "SLAs" },
                { k: "Visible via", v: "Dashboards" },
              ].map((b, i) => (
                <div key={i} className="bg-white p-6">
                  <div className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-[#01080E]/50">
                    {b.k}
                  </div>
                  <div className="mt-2 font-display text-2xl font-bold text-[#01080E]">{b.v}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export function Services() {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative py-24 md:py-32 bg-[#FAFAF8] border-t border-[#01080E]/10"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-3 mb-5">
            <span className="block h-px w-8 bg-[#2AC0A5]" />
            <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[#01080E]">
              What we manage
            </span>
          </div>
          <h2 className="font-display text-3xl md:text-5xl font-bold leading-[1.05] text-[#01080E]">
            Four operations. One governed engagement.
          </h2>
          <p className="mt-5 text-lg text-[#01080E]/75 leading-relaxed">
            We run the customer-facing operations that decide whether your industrial business
            actually compounds, across the full lead-to-loyalty lifecycle.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-px bg-[#01080E]/10 border border-[#01080E]/10">
          {SERVICES.map((s) => (
            <article
              key={s.code}
              data-testid={`service-card-${s.code}`}
              className="group bg-white p-8 md:p-10 hover:bg-[#01080E] transition-colors duration-300"
            >
              <div className="flex items-start justify-between mb-6">
                <span className="font-mono-tech text-xs tracking-[0.2em] text-[#01080E]/40 group-hover:text-[#7BDCB5] transition-colors">
                  / {s.code}
                </span>
                <span className="inline-flex items-center justify-center w-9 h-9 border border-[#01080E]/15 group-hover:border-[#2AC0A5] group-hover:bg-[#2AC0A5] transition-colors">
                  <ArrowUpRight size={16} className="text-[#01080E] group-hover:text-[#01080E]" />
                </span>
              </div>
              <h3 className="font-display text-2xl md:text-[28px] font-semibold leading-tight text-[#01080E] group-hover:text-white transition-colors">
                {s.title}
              </h3>
              <p className="mt-4 text-[15px] leading-relaxed text-[#01080E]/70 group-hover:text-white/80 transition-colors">
                {s.detail}
              </p>
              {s.bullets && (
                <ul className="mt-6 space-y-2.5 border-t border-[#01080E]/10 group-hover:border-white/15 pt-5 transition-colors">
                  {s.bullets.map((b, i) => (
                    <li
                      key={i}
                      className="flex items-start gap-3 text-sm text-[#01080E]/80 group-hover:text-white/85 transition-colors"
                    >
                      <span
                        aria-hidden
                        className="mt-2 inline-block h-1.5 w-1.5 bg-[#2AC0A5] flex-shrink-0"
                      />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              )}
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

export function HowWeRunIt() {
  return (
    <section
      id="how-we-run-it"
      data-testid="how-we-run-it-section"
      className="relative py-24 md:py-32 bg-white border-t border-[#01080E]/10"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            <div className="inline-flex items-center gap-3 mb-5">
              <span className="block h-px w-8 bg-[#2AC0A5]" />
              <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[#01080E]">
                How we run it
              </span>
            </div>
            <h2 className="font-display text-3xl md:text-5xl font-bold leading-[1.05] text-[#01080E]">
              Your operation,
              <br />
              <span className="text-[#2AC0A5]">governed end-to-end.</span>
            </h2>
            <p className="mt-5 text-lg text-[#01080E]/75 leading-relaxed">
              A clear operating model: onsite leadership, dedicated team, integrated tech and a
              fixed governance rhythm. No black box.
            </p>
            <div
              aria-hidden
              className="mt-10 hidden lg:block relative h-[320px] border border-[#01080E]/10 overflow-hidden bg-[#01080E]"
            >
              <div className="absolute inset-0 grid-texture-dark opacity-60" />
              <div className="absolute inset-0 flex items-center justify-center p-8">
                <div className="text-center">
                  <div className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-[#7BDCB5]">
                    Operating Cadence
                  </div>
                  <div className="mt-4 font-display text-5xl font-bold text-white leading-none">
                    Daily ·<br />Weekly ·<br />Monthly
                  </div>
                  <div className="mt-6 inline-flex items-center gap-2 font-mono-tech text-[11px] uppercase tracking-[0.2em] text-white/70">
                    <span className="h-1.5 w-1.5 rounded-full bg-[#2AC0A5]" />
                    Under contractual SLA
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <ol className="space-y-px bg-[#01080E]/10 border border-[#01080E]/10">
              {HOW_WE_RUN_IT.map((p, i) => (
                <li
                  key={i}
                  data-testid={`run-step-${i + 1}`}
                  className="bg-white p-6 md:p-8 grid grid-cols-[auto_1fr] gap-6 items-start hover:bg-[#FAFAF8] transition-colors"
                >
                  <span className="font-mono-tech text-sm font-semibold text-[#2AC0A5] pt-1 min-w-[42px]">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div>
                    <h3 className="font-display text-xl md:text-2xl font-semibold text-[#01080E]">
                      {p.label}
                    </h3>
                    <p className="mt-2 text-[15px] leading-relaxed text-[#01080E]/70">{p.detail}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </div>
    </section>
  );
}

export function WhyUs() {
  return (
    <section
      id="why-us"
      data-testid="why-us-section"
      className="relative py-24 md:py-32 bg-[#FAFAF8] border-t border-[#01080E]/10"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="max-w-3xl mb-14">
          <div className="inline-flex items-center gap-3 mb-5">
            <span className="block h-px w-8 bg-[#2AC0A5]" />
            <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[#01080E]">
              Why choose Thoratech
            </span>
          </div>
          <h2 className="font-display text-3xl md:text-5xl font-bold leading-[1.05] text-[#01080E]">
            Six reasons leaders pick us
            <br />
            for their customer operations.
          </h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[#01080E]/10 border border-[#01080E]/10">
          {WHY_US.map((w, i) => (
            <div
              key={i}
              data-testid={`why-us-card-${i + 1}`}
              className="bg-white p-8 md:p-10 group hover:bg-white relative overflow-hidden"
            >
              <div className="flex items-start gap-4">
                <span className="inline-flex items-center justify-center min-w-[40px] h-10 bg-[#2AC0A5]/15 text-[#01080E] font-mono-tech text-xs font-bold">
                  0{i + 1}
                </span>
                <div>
                  <h3 className="font-display text-xl md:text-2xl font-semibold text-[#01080E] leading-tight">
                    {w.title}
                  </h3>
                  <p className="mt-3 text-[15px] leading-relaxed text-[#01080E]/70">{w.detail}</p>
                </div>
              </div>
              <Check
                size={120}
                aria-hidden
                className="absolute -right-6 -bottom-8 text-[#2AC0A5]/0 group-hover:text-[#2AC0A5]/10 transition-colors duration-500"
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
